import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import Button from '@ingka/button';
import { Sheets, ModalHeader, ModalFooter, ModalBody } from '@ingka/modal';

import { useAppDispatch } from '@/redux/hooks';
import { useGetCodeFilters } from '@/redux/features/code-filters/hooks';
import { setFilters } from '@/redux/features/code-filters/slice';
import { Switch } from '@/components/controls/switch';
import { DropdownCountries } from '@/components/controls/dropdown-countries';
import { DropdownSites } from '@/components/controls/dropdown-site';
import { DropdownComponents } from '@/components/controls/dropdown-component';
import { DropdownDepartment } from '@/components/controls/dropdown-department';
import { DropdownCarrierType } from '@/components/controls/dropdown-carrier-type';
import { DropdownCarrierSize } from '@/components/controls/dropdown-carrier-size';
import { Reset } from '@/components/page-widgets/codes/reset';
import { DropdownCommunicationPurpose } from '@/components/controls/dropdown-communication-purpose';
import { DropdownSubcategory } from '@/components/controls/dropdown-subcategory';

type CodesFilterSiderProps = ModalComponentBaseProps;

const CodesFilterSider: FC<CodesFilterSiderProps> = ({
  handleDismiss,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    onlyMyCodes,
    country,
    // date,
    location,
    site,
    communicationPurpose,
    subcategory,
    component,
    department,
    carrierType,
    carrierSize,
  } = useGetCodeFilters();
  const [draftOnlyMyCodes, setDraftOnlyMyCodes] = useState(onlyMyCodes);
  const [draftCountry, setDraftCountry] = useState(country);
  const [draftLocation, setDraftLocation] = useState(location);
  const [draftSite, setDraftSite] = useState(site);
  const [draftCommunicationPurpose, setDraftCommunicationPurpose] =
    useState(communicationPurpose);
  const [draftSubcategory, setDraftSubcategory] = useState(subcategory);
  const [draftDepartment, setDraftDepartment] = useState(department);
  const [draftCarrierType, setDraftCarrierType] = useState(carrierType);
  const [draftCarrierSize, setDraftCarrierSize] = useState(carrierSize);

  // const [draftDate, setDraftDate] = useState<{ from?: number; to?: number }>(
  //   date,
  // );
  const [draftComponent, setDraftComponent] = useState(component);

  const handleCountry = (country: string[]) => {
    setDraftCountry(country);
    setDraftLocation([]);
  };

  const handleLocation = (location: string[]) => {
    setDraftLocation(location);
  };

  const handleSite = (site: string[]) => {
    setDraftSite(site);
  };

  const handleComponent = (component: string[]) => {
    setDraftComponent(component);
  };

  const handleDepartment = (department: string[]) => {
    setDraftDepartment(department);
  };
  const handleCarrierType = (carrierType: string[]) => {
    setDraftCarrierType(carrierType);
  };
  const handleCarrierSize = (carrierSize: string[]) => {
    setDraftCarrierSize(carrierSize);
  };
  const handleCommunicationPurpose = (communicationPurpose: string[]) => {
    setDraftCommunicationPurpose(communicationPurpose);
  };
  const handleSubcategory = (subcategory: string[]) => {
    setDraftSubcategory(subcategory);
  };

  const handleSubmitFilters = () => {
    dispatch(
      setFilters({
        onlyMyCodes: draftOnlyMyCodes,
        country: draftCountry,
        location: draftLocation,
        site: draftSite,
        // date: draftDate,
        communicationPurpose: draftCommunicationPurpose,
        subcategory: draftSubcategory,
        component: draftComponent,
        department: draftDepartment,
        carrierType: draftCarrierType,
        carrierSize: draftCarrierSize,
      })
    );
    handleDismiss();
  };
  const isDirty =
    onlyMyCodes !== draftOnlyMyCodes ||
    country !== draftCountry ||
    location !== draftLocation ||
    site !== draftSite ||
    component !== draftComponent ||
    department !== draftDepartment ||
    carrierType !== draftCarrierType ||
    carrierSize !== draftCarrierSize ||
    communicationPurpose !== draftCommunicationPurpose ||
    subcategory !== draftSubcategory;
  // date.from !== draftDate.from ||
  // date.to !== draftDate.to;

  return (
    <Sheets
      {...rest}
      open={true}
      alignment="right"
      aria-label={t('label.filter')}
      footer={
        <ModalFooter>
          <Button
            data-testid="confirmButton"
            text={t('label.confirm')}
            type="primary"
            onClick={handleSubmitFilters}
            disabled={!isDirty}
          />
        </ModalFooter>
      }
      header={
        <ModalHeader
          ariaCloseTxt={t('label.close')}
          title={t('label.filter')}
        />
      }
      size="small"
    >
      <ModalBody>
        <Switch
          id="showOnlyMyCodes"
          label={t('label.code.myCodes')}
          value={draftOnlyMyCodes}
          onChange={setDraftOnlyMyCodes}
        />
        <hr className="my-4" />
        <div className="grid grid-cols-2 gap-6">
          <DropdownCountries
            position="bottom-left"
            value={draftCountry}
            onChange={handleCountry}
          />
          <DropdownSites
            position="bottom-left"
            value={draftSite}
            onChange={handleSite}
            filterCountries={country}
          />
          <DropdownCommunicationPurpose
            position="bottom-left"
            value={draftCommunicationPurpose}
            onChange={handleCommunicationPurpose}
          />
          <DropdownSubcategory
            position="bottom-left"
            value={draftSubcategory}
            onChange={handleSubcategory}
          />
          <DropdownComponents
            position="bottom-left"
            value={draftComponent}
            onChange={handleComponent}
          />
          <DropdownDepartment
            position="bottom-left"
            value={draftDepartment}
            onChange={handleDepartment}
          />
          <DropdownCarrierType
            position="bottom-left"
            value={draftCarrierType}
            onChange={handleCarrierType}
          />
          <DropdownCarrierSize
            position="bottom-left"
            value={draftCarrierSize}
            onChange={handleCarrierSize}
          />
        </div>
        <hr className="my-4" />
        <Reset />
      </ModalBody>
    </Sheets>
  );
};

export default CodesFilterSider;
